/** /reflect/history - past reflections, newest first, each
 *  expandable to full content. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Moon } from "lucide-react";
import { api, type ReflectionRow } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

const MOOD_DOT: Record<string, string> = {
  great: "bg-emerald-500",
  good: "bg-lime-500",
  neutral: "bg-slate-400",
  low: "bg-amber-500",
  rough: "bg-rose-500",
};

function Row({ r }: { r: ReflectionRow }) {
  const [open, setOpen] = useState(false);
  const blocks: Array<[string, string | null]> = [
    ["Highlights", r.highlights_md],
    ["Lows", r.lows_md],
    ["Grateful for", r.grateful_for_md],
    ["Tomorrow's focus", r.tomorrow_focus_md],
    ["Open loops", r.open_loops_md],
  ];
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40"
      >
        <span
          className={`h-3 w-3 rounded-full ${
            r.mood ? MOOD_DOT[r.mood] ?? "bg-slate-300" : "bg-slate-300"
          }`}
        />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {r.reflection_date}
          </span>
          <span className="ml-2 text-xs text-slate-500">
            {r.mood ?? "no mood"}
            {r.energy_level ? ` · energy ${r.energy_level}/5` : ""}
          </span>
          {r.tags.length > 0 && (
            <span className="ml-2 text-xs text-slate-400">
              {r.tags.slice(0, 5).join(", ")}
            </span>
          )}
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-2 border-t border-slate-100 dark:border-slate-800 text-sm">
          {blocks
            .filter(([, v]) => v)
            .map(([label, v]) => (
              <div key={label}>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  {label}
                </p>
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {v}
                </p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export function ReflectHistory() {
  const [rows, setRows] = useState<ReflectionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .reflectionsList()
      .then((d) => {
        setRows(d.reflections);
        setErr(null);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-indigo-500" />
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Past reflections
          </h1>
        </div>
        <Link
          to="/reflect"
          className="text-sm text-indigo-600 dark:text-indigo-300 hover:underline"
        >
          Back to today
        </Link>
      </div>

      <TeachingBanner id="reflect_history_overview" />

      {loading && (
        <p className="text-sm text-slate-400">Loading...</p>
      )}
      {err && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>
      )}
      {!loading && !err && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-sm text-slate-500">
          No reflections yet. Start tonight.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          <Row key={r.id} r={r} />
        ))}
      </div>
    </div>
  );
}
